import { useEffect, useState } from 'react';
import type { PulseStats } from '../types';
import { PulseOdometer } from './PulseOdometer';

interface PulseHeaderProps {
  stats: PulseStats;
  connected: boolean;
}

/**
 * Minimal top bar — wordmark, live clock, connection indicator, active users, upload rate.
 * Uses gradient background that fades into the grid.
 */
export function PulseHeader({ stats, connected }: PulseHeaderProps) {
  const [clock, setClock] = useState(formatTime());

  useEffect(() => {
    const interval = setInterval(() => setClock(formatTime()), 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <header className="pulse-header relative z-10 flex items-center justify-between px-5 py-3">
      {/* Left: wordmark */}
      <div className="flex items-center gap-3">
        <span className="text-lg font-bold tracking-wide text-white/90">
          TIPMI <span className="font-light text-[#0194f5]">PULSE</span>
        </span>
        {/* Connection indicator */}
        <span
          className={`size-2 rounded-full ${connected ? 'bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.7)]' : 'bg-red-500 animate-pulse'}`}
          title={connected ? 'Connected' : 'Disconnected'}
        />
      </div>

      {/* Right: stats + clock */}
      <div className="flex items-center gap-6 text-xs text-white/70">
        <div className="flex items-center gap-1.5">
          <svg className="size-3.5 text-emerald-400" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm3.5 9H8.2a.5.5 0 0 1-.4-.2l-1.6-2a.5.5 0 0 1 .8-.6L8.4 8H11.5a.5.5 0 0 1 0 1z" />
          </svg>
          <span className="tabular-nums">
            <PulseOdometer value={stats.activeUsers} /> active
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          <svg className="size-3.5 text-[#0194f5]" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 1l2 5h5l-4 3.5 1.5 5L8 11.5 3.5 14.5 5 9.5 1 6h5z" />
          </svg>
          <span className="tabular-nums">
            <PulseOdometer value={stats.uploadsPerMinute} />/min
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          <svg className="size-3.5 text-amber-400" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 2a6 6 0 1 0 0 12A6 6 0 0 0 8 2zm0 1a5 5 0 1 1 0 10A5 5 0 0 1 8 3zm-.5 2v3.5l2.5 1.5.5-.8-2-1.2V5z" />
          </svg>
          <span className="tabular-nums font-medium text-white/90">{clock}</span>
        </div>
      </div>
    </header>
  );
}

function formatTime(): string {
  return new Date().toLocaleTimeString('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}
